import { useMemo, useCallback, useState, useEffect } from "react";
import { useGT } from "gt-react";
import { useAppStore, useResultStore, useHttpDebugStore } from "../store";
import { AlertCircle, Copy, Check, Terminal, Braces, ShieldAlert, AlertTriangle } from "lucide-react";
import { CodeMirrorEditor } from "./CodeMirrorEditor";
import { HeadersTable, Section, CopyButton, tryFormatJson, buildCurl } from "./ResultShared";
import { LazyRelationalResultGrid } from "./LazyRelationalResultGrid";
import { json } from "@codemirror/lang-json";
import { extractSqlTabularRows } from "../resultTypes";

const jsonExtensions = [json()];

function isCorsLikeError(error: string): boolean {
  return /CORS error:|Failed to fetch|NetworkError|Load failed/i.test(error);
}

// ── Main Component ──────────────────────────────────────────────────

export function ResultPane() {
  const t = useGT();
  const result = useResultStore((s) => s.result);
  const isExecuting = useResultStore((s) => s.isExecuting);
  const error = useResultStore((s) => s.error);
  const debug = useHttpDebugStore((s) => s.debug);
  const setShowRuntimeDialog = useAppStore((s) => s.setShowRuntimeDialog);
  const [copied, setCopied] = useState(false);
  const [displayValue, setDisplayValue] = useState<string | null>(null);

  const isJsonResponse = useMemo(() => {
    if (!result) return true;
    if (result.responseContentType) return result.responseContentType.includes("json");
    return result.data !== undefined;
  }, [result]);

  const rawResult = useMemo(() => {
    if (!result) return null;
    if (result.requestType === "sql" && result.responseBody !== undefined) {
      return result.responseBody;
    }
    if (result.responseBody !== undefined && !isJsonResponse) {
      return result.responseBody;
    }
    try {
      const output: Record<string, unknown> = {};
      if (result.data !== undefined) output.data = result.data;
      if (result.errors?.length) output.errors = result.errors;
      return JSON.stringify(Object.keys(output).length ? output : result, null, 2);
    } catch {
      return result.responseBody ?? String(result);
    }
  }, [result, isJsonResponse]);

  const gqlErrors = useMemo(() => {
    if (!result?.errors?.length) return null;
    return result.errors as Array<{
      message?: string;
      locations?: Array<{ line: number; column: number }>;
      path?: Array<string | number>;
      extensions?: Record<string, unknown>;
    }>;
  }, [result]);

  const sqlRows = useMemo(() => extractSqlTabularRows(result), [result]);
  const sqlRowCount = result?.requestType === "sql"
    ? result.rowCount ?? sqlRows?.length ?? 0
    : null;

  const shownValue = displayValue ?? rawResult;

  const responseSize = useMemo(() => {
    if (!shownValue) return null;
    const bytes = new Blob([shownValue]).size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, [shownValue]);

  useEffect(() => {
    setDisplayValue(null);
  }, [result]);

  const handleCopy = useCallback(async () => {
    if (!shownValue) return;
    await navigator.clipboard.writeText(shownValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [shownValue]);

  const handlePrettify = useCallback(() => {
    if (!shownValue) return;
    try {
      const prettified = JSON.stringify(JSON.parse(shownValue), null, 2);
      setDisplayValue(prettified);
    } catch { /* ignore */ }
  }, [shownValue]);

  const statusColor =
    result?.statusCode != null
      ? result.statusCode >= 200 && result.statusCode < 300
        ? "text-success"
        : result.statusCode >= 400
          ? "text-danger"
          : "text-warning"
      : "text-zinc-500";

  const hasResponseHeaders = debug?.response?.headers && Object.keys(debug.response.headers).length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Status bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-raised px-4 py-1.5">
        <span className="text-xs font-medium text-zinc-500">{t("Result")}</span>
        <div className="flex items-center gap-3">
          {result && (
            <>
              {result.statusCode != null && (
                <span className={`text-xs font-mono font-medium ${statusColor}`}>
                  {result.statusCode}
                </span>
              )}
              {result.durationMs != null && (
                <span className="text-xs text-zinc-500">{result.durationMs}ms</span>
              )}
              {result.requestType === "sql" && result.command && (
                <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">
                  {result.command}
                </span>
              )}
              {result.requestType === "sql" && sqlRowCount != null && (
                <span className="text-xs text-zinc-500">
                  {sqlRowCount} rows
                </span>
              )}
              {responseSize && (
                <span className="text-xs text-zinc-600">{responseSize}</span>
              )}
            </>
          )}
          {shownValue && (
            <>
              {isJsonResponse && (
                <button
                  onClick={handlePrettify}
                  className="rounded p-1 text-zinc-600 hover:bg-surface-raised hover:text-zinc-400"
                  title={t("Prettify JSON")}
                >
                  <Braces size={12} />
                </button>
              )}
              {debug && (
                <CopyButton text={buildCurl(debug)} label="cURL" />
              )}
                <button
                  onClick={handleCopy}
                  className="rounded p-1 text-zinc-600 hover:bg-surface-raised hover:text-zinc-400"
                  title={t("Copy result")}
                >
                {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Collapsible Request section */}
      {!isExecuting && debug && (
        <Section
          title={t("Request")}
          badge={
            <span className="ml-1.5 rounded bg-warning-bg px-1.5 py-0.5 text-[9px] font-bold normal-case tracking-normal text-warning">
              {debug.request.method}
            </span>
          }
        >
          <div className="mb-2 break-all font-mono text-[11px] text-zinc-300">
            {debug.request.url}
          </div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-600">{t("Headers")}</div>
          <HeadersTable headers={debug.request.headers} />
          {debug.request.body && (
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-600">{t("Body")}</span>
                <CopyButton text={debug.request.body} />
              </div>
              <pre className="max-h-32 overflow-auto rounded bg-surface-raised p-2 font-mono text-[11px] leading-relaxed text-zinc-400">
                {tryFormatJson(debug.request.body)}
              </pre>
            </div>
          )}
        </Section>
      )}

      {/* Collapsible Response Headers section */}
      {!isExecuting && hasResponseHeaders && (
        <Section
          title={t("Response Headers")}
          badge={
            <span className="ml-1 text-[9px] font-normal normal-case tracking-normal text-zinc-600">
              ({Object.keys(debug!.response!.headers).length})
            </span>
          }
        >
          <HeadersTable headers={debug!.response!.headers} />
        </Section>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden bg-surface">
        {isExecuting && (
          <div className="flex h-full items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-600 border-t-warning" />
              {t("Executing...")}
              </div>
            </div>
        )}

        {error && !isExecuting && (
          <div className="p-4">
            {isCorsLikeError(error) ? (
              <div className="space-y-3 rounded bg-danger-bg p-3">
                <div className="flex items-start gap-2.5 text-danger">
                  <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                  <div className="space-y-2">
                    <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{error}</pre>
                    <p className="text-xs text-zinc-400">
                      {t("The server doesn't allow cross-origin requests from the browser.")}
                      {" "}
                      {t("Use the Local Runtime to run requests locally without browser CORS limits.")}
                    </p>
                    <button
                      onClick={() => setShowRuntimeDialog(true)}
                      className="rounded bg-brand px-3 py-1 text-xs font-medium text-zinc-900 hover:bg-brand-light"
                    >
                      {t("Open Local Runtime")}
                    </button>
                    <p className="text-[10px] text-zinc-600">
                      {t("Install and pair the Local Runtime to route requests locally instead of through the browser.")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded bg-danger-bg p-3 text-danger">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{error}</pre>
              </div>
            )}
          </div>
        )}

        {!isExecuting && !error && gqlErrors && (
          <div className="shrink-0 border-b border-border">
            {gqlErrors.map((err, i) => (
              <div key={i} className="flex gap-2.5 border-b border-border/50 px-4 py-2.5 last:border-b-0">
                <AlertTriangle size={13} className="mt-0.5 shrink-0 text-danger" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-danger">{String(err.message)}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-500">
                    {err.locations?.map((loc, j) => (
                      <span key={j} className="font-mono">line {loc.line}:{loc.column}</span>
                    ))}
                    {err.path && <span className="font-mono">{err.path.map((part) => String(part)).join(".")}</span>}
                    {err.extensions?.code !== undefined && (
                      <span className="rounded bg-danger-bg px-1 py-0.5 font-mono text-danger">
                        {String(err.extensions.code)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isExecuting && !error && sqlRows && (
          <LazyRelationalResultGrid rows={sqlRows} />
        )}

        {!isExecuting && !error && shownValue && !sqlRows && (
          isJsonResponse ? (
            <CodeMirrorEditor value={shownValue} readOnly extensions={jsonExtensions} />
          ) : (
            <CodeMirrorEditor value={shownValue} readOnly extensions={[]} />
          )
        )}

        {!isExecuting && !error && !result && (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-zinc-700">
              <Terminal size={28} />
            <div className="text-center text-sm">{t("Execute a query to see results")}</div>
            <kbd className="rounded border border-zinc-800 bg-surface-raised px-2 py-0.5 text-xs text-zinc-600">⌘ + Enter</kbd>
          </div>
        )}
      </div>
    </div>
  );
}
